/**
 * The one origin-specific input of the surface host: the fetch policy port, and the per-mount
 * `fetch` built over it (kernel apps, WS-14 section 3.5; feature 2026-09-18-audience-bound-dp-token
 * D-1, D-3, D-11; layered shell surfaces R-127).
 *
 * Each host document builds one {@link OriginFetchPolicy} for its own origin: the shell over
 * `createAppFetch` and its app-token ledger (WS-16), the kernel SPA over the kernel token (WS-15).
 * This module decides which targets the policy may see, and the policy decides which bearer each
 * target gets. A cross-origin target never reaches a policy, so no policy can put a bearer on
 * another origin (security branch S3).
 */
import { RESERVED_PLATFORM_PREFIXES, matchesPrefix } from '@zion/sdk-cp-dp-contracts';
import { ADMIN_API_PREFIX } from '@zion/sdk-dp-admin-contracts';
import { SHELL_ATTEMPT_TIMEOUT_MS, createBodyDeadline } from '@zion/shell-system-surfaces/body-deadline';
import { disposedError, type MountScope } from './kit.js';

/**
 * Who the bearer that a policy attaches now belongs to: the access token's `sub` and `orgId`
 * claims, each `null` when the claim is absent. The preferences store compares it with the session
 * before each attempt, so a bearer of another identity (another tab) sends nothing.
 */
export interface BearerIdentity {
  readonly sub: string | null;
  readonly orgId: string | null;
}

/**
 * The fetch rules of one origin, which the host builds and the services consume.
 *
 * - `platformFetch` sends a platform route with this origin's session bearer. It owns the 401
 *   refresh, the cold-start retry, the maintenance window and the wait for the response headers
 *   of each attempt. The services read branding, preferences and settings through it.
 * - `appFetch(appUuid, attemptTimeoutMs)` answers the `fetch` of one mount for its same-origin
 *   targets. It decides the bearer per class: the session bearer for a `'reserved'` target, the
 *   app's own token for an `'app'` target while the app is retained, and no bearer for a `null`
 *   `appUuid`'s `'app'` target. A `'kernel-only'` target is the policy's to rule: the shell
 *   origin's policy answers a local 403 `KernelOriginRequiredBody` and sends nothing while the
 *   kernel origin is on, and sends it as a `'reserved'` target while the switch is off; the kernel
 *   origin's policy sends it with its own bearer. It must put no bearer on a target of another
 *   origin; {@link createMountFetch} never hands it one. With `attemptTimeoutMs`, a finite number
 *   above 0 and at most 2147483647, each network attempt of one call waits at most that long for
 *   its response headers, and each retry has a fresh deadline (R-127, the shell's
 *   `createAppFetch(appUuid, attemptTimeoutMs)`). The policy sets no deadline over the wait between
 *   two attempts. Without it, no attempt has a deadline. A value out of range throws `RangeError`
 *   when `appFetch` is called. A policy may set `cache` on the `init` of an `'app'` GET or HEAD
 *   whose caller set no `cache`; the shell's policy sets `'no-store'` there (#3291). A caller's
 *   `cache` stays as the caller set it.
 * - `retainApp(appUuid)` counts one live mount of the app and answers an idempotent release.
 *   `retainApp(null)` counts nothing and answers a no-op.
 * - `liveIdentity()` answers the identity of the bearer that `platformFetch` would attach now, or
 *   `null` when it would attach none.
 *
 * The services never dispose a policy; its builder does.
 */
export interface OriginFetchPolicy {
  readonly platformFetch: typeof fetch;
  appFetch(appUuid: string | null, attemptTimeoutMs?: number): typeof fetch;
  retainApp(appUuid: string | null): () => void;
  liveIdentity(): BearerIdentity | null;
}

/**
 * Which bearer class a mount's `fetch` target belongs to (D-11, R-033; kernel apps WS-16b).
 *
 * - `'kernel-only'`: a same-origin path under `ADMIN_API_PREFIX` (`/v1/admin`). The admin routes
 *   belong to the kernel origin. The policy rules it (see {@link OriginFetchPolicy}).
 * - `'reserved'`: any other same-origin path under `RESERVED_PLATFORM_PREFIXES`. The host's own
 *   platform routes and the app-token exchange. The policy gives it the session bearer.
 * - `'app'`: any other same-origin path. The hosted app's own backend. The policy gives it the app
 *   token.
 * - `'cross-origin'`: an origin other than the document's. It carries no bearer.
 */
export type FetchTarget = 'reserved' | 'app' | 'cross-origin' | 'kernel-only';

/**
 * True for a `Request` of another realm, such as an iframe or another window: a value that
 * `instanceof Request` refuses and whose `Object.prototype.toString` tag reads `Request`. This
 * realm's `Request` reader cannot read its URL, and `String(input)` gives `[object Request]`, so
 * the value takes the class that carries no bearer (R-125). A value that forges the tag takes that
 * class too, which only takes a bearer away. It is the shell's `isForeignRequest` in
 * `api/client.ts`.
 *
 * It throws what the read of the prototype chain or of the tag throws, as a `Proxy` can.
 */
function isForeignRequest(input: RequestInfo | URL): boolean {
  return !(input instanceof Request) && Object.prototype.toString.call(input) === '[object Request]';
}

/**
 * The URL text of a `fetch` target, by the conversion the global `fetch` applies: a string as it
 * is, a `Request` of this realm by its `url`, and any other value, a `URL` among them, by
 * `String(input)`. A `URL` from another realm and an object with its own `toString` therefore read
 * as the text that `fetch` would send. A caller checks {@link isForeignRequest} first, because a
 * `Request` of another realm reads here as `[object Request]`. It throws what `String(input)`
 * throws.
 */
function targetText(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof Request) return input.url;
  return String(input);
}

/** The class of one URL text, and the value that a call must send so the network sees that text. */
interface ResolvedTarget {
  readonly target: FetchTarget;
  /** The absolute URL the text resolved to, or the text itself when no base resolves it. */
  readonly href: string;
}

/** The class of `text` against `documentHref`, by the rules that {@link classifyFetchTarget} states. */
function resolveTarget(text: string, documentHref: string): ResolvedTarget {
  let base: URL;
  try {
    base = new URL(documentHref);
  } catch {
    return { target: 'cross-origin', href: text };
  }
  if (base.origin === 'null') return { target: 'cross-origin', href: text };
  let resolved: URL;
  try {
    resolved = new URL(text, base);
  } catch {
    return { target: 'app', href: text };
  }
  if (resolved.origin !== base.origin) return { target: 'cross-origin', href: resolved.href };
  if (matchesPrefix(resolved.pathname, ADMIN_API_PREFIX)) return { target: 'kernel-only', href: resolved.href };
  const reserved = RESERVED_PLATFORM_PREFIXES.some((prefix) => matchesPrefix(resolved.pathname, prefix));
  return { target: reserved ? 'reserved' : 'app', href: resolved.href };
}

/**
 * Classify a `fetch` target into the four bearer classes. It is the shell's
 * `classifyAppFetchTarget` with the document URL as an argument in place of `window.location`, so
 * the two answer alike for every input and every parsed base, a `Request` of another realm and a
 * read that throws among them (R-125).
 *
 * @param input The target a caller handed `fetch`. A string reads as it is, a `Request` of this
 *   realm by its `url`, and any other value, a `URL` among them, by `String(input)`, the
 *   conversion the global `fetch` applies. It copies no `Request`, so the body of the caller's
 *   `Request` stays unread.
 * @param documentHref The URL of the document that makes the call, `location.href` in production.
 *   The target resolves against it, so the protocol-relative form `//host/path` names another
 *   origin. The browser's own `fetch` resolves a relative text against `document.baseURI`, which a
 *   `<base>` element can move, so a caller that sends must send the absolute URL this rule
 *   resolved, as {@link createMountFetch} does.
 * @returns In this order:
 * - `'cross-origin'` when `documentHref` does not parse or has an opaque origin. No origin can
 *   then be the document's, so no bearer may ride (defaults row "Refuse over accept").
 * - `'cross-origin'` when the read of the target throws: a `toString` or a `url` reader that
 *   throws, or a `Proxy` whose prototype read throws. The target has no text that can be checked.
 * - `'cross-origin'` for a `Request` of another realm, which `instanceof Request` refuses and whose
 *   `Object.prototype.toString` tag reads `Request`, a forged tag among them. This realm cannot
 *   read its URL, so no bearer may ride (R-125, defaults row "Refuse over accept").
 * - `'app'` for a target that no base can resolve. It reaches no origin of its own, so it reaches
 *   no reserved or admin route either.
 * - `'cross-origin'` when the resolved target's origin differs from the document's.
 * - `'kernel-only'` when the resolved pathname matches `ADMIN_API_PREFIX` under the segment-aware
 *   `matchesPrefix`, so `/v1/admin/users` is `'kernel-only'` and `/v1/administer` is `'app'`.
 * - `'reserved'` when it matches another entry of `RESERVED_PLATFORM_PREFIXES`, so `/v1/authz` is
 *   `'app'` and `/v1/auth/app-token` is `'reserved'`.
 * - `'app'` for every other same-origin target.
 *
 * The answer does not depend on the kernel switch. It reads no global, performs no request and
 * never throws.
 */
export function classifyFetchTarget(input: RequestInfo | URL, documentHref: string): FetchTarget {
  let text: string;
  try {
    if (typeof input !== 'string' && isForeignRequest(input)) return 'cross-origin';
    text = targetText(input);
  } catch {
    return 'cross-origin';
  }
  return resolveTarget(text, documentHref).target;
}

/**
 * The longest wait, in milliseconds, for the response headers of a system mount's `fetch` call and
 * for the body of one services JSON call once its headers are in. It is the shell's
 * `SHELL_ATTEMPT_TIMEOUT_MS` from `@zion/shell-system-surfaces/body-deadline`, the one home of the
 * value: 30 seconds, which R-64 chose and R-127 kept.
 */
export const SERVICES_FETCH_TIMEOUT_MS: number = SHELL_ATTEMPT_TIMEOUT_MS;

/** The largest delay, in milliseconds, that `setTimeout` keeps: 2^31 - 1. */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * The signal that `fetch` obeys for one call, by the Fetch rule: `init.signal` when it is not
 * `undefined` (`null` means no signal), else the signal of a `Request` input, else `null`.
 */
function callerSignalOf(input: RequestInfo | URL, init: RequestInit | undefined): AbortSignal | null {
  if (init?.signal !== undefined) return init.signal;
  return input instanceof Request ? input.signal : null;
}

/** Throw `RangeError` unless `timeoutMs` is a delay that `setTimeout` keeps and that is above 0. */
function assertTimerDelay(timeoutMs: number): void {
  if (!(Number.isFinite(timeoutMs) && timeoutMs > 0 && timeoutMs <= MAX_TIMER_DELAY_MS)) {
    throw new RangeError(`timeoutMs must be a finite number above 0 and at most ${MAX_TIMER_DELAY_MS}, got ${timeoutMs}`);
  }
}

/**
 * Wrap `inner` so each call waits at most `timeoutMs` for its response headers (R-127).
 *
 * The deadline covers one call of `inner`, from the send to the response headers. Use it over one
 * network attempt, such as the `fetch` of a policy that sends each call once. Over an `inner` that
 * retries inside one call (a cold start, a maintenance window), the retries and the waits between
 * them would share the one deadline, so a policy that retries applies its deadline per attempt
 * itself (see `OriginFetchPolicy.appFetch`).
 * The timer stops when `inner` settles, so the body of a stream stays open for as long as its
 * server sends it, and the caller's own signal still aborts that body.
 *
 * @param inner The `fetch` to bound, such as one network attempt of a policy.
 * @param timeoutMs A finite number above 0 and at most 2147483647, the largest delay `setTimeout`
 *   keeps. {@link SERVICES_FETCH_TIMEOUT_MS} in production; a test shortens it.
 * @returns A `fetch` that hands `inner` a copy of `init` whose `signal` joins the caller's signal
 *   (the `init.signal`, else the signal of a `Request` input) and the deadline.
 * - Headers within the deadline: the response, unchanged.
 * - The deadline passes first: the call rejects with a `TimeoutError` `DOMException`.
 * - A caller signal that is already aborted: the call rejects with its reason and sends nothing.
 * - `inner` rejects: that error, unchanged.
 * Every other `init` member passes through unchanged. It holds no state between calls.
 * @throws `RangeError` at construction when `timeoutMs` is out of range.
 */
export function withAttemptTimeout(inner: typeof fetch, timeoutMs: number): typeof fetch {
  assertTimerDelay(timeoutMs);
  return async (input, init) => {
    const deadline = new AbortController();
    const callerSignal = callerSignalOf(input, init);
    const signal = callerSignal === null ? deadline.signal : AbortSignal.any([callerSignal, deadline.signal]);
    if (signal.aborted) throw signal.reason;
    const timer = setTimeout(() => {
      deadline.abort(new DOMException(`no response headers within ${timeoutMs} ms`, 'TimeoutError'));
    }, timeoutMs);
    try {
      return await inner(input, { ...init, signal });
    } finally {
      clearTimeout(timer);
    }
  };
}

/**
 * The rejection of a services JSON call whose answer is not 2xx. It has the shape of the shell's
 * `ApiError` in `api/client.ts`, so a caller that reads `name === 'ApiError'` and `statusCode`
 * reads both alike.
 */
export class ApiError extends Error {
  override readonly name = 'ApiError';

  /**
   * @param statusCode The HTTP status of the answer.
   * @param message The path and the status, never a body.
   * @param body The parsed error body, `null` when the call did not read one.
   */
  constructor(
    readonly statusCode: number,
    message: string,
    readonly body: unknown = null,
  ) {
    super(message);
  }
}

/**
 * One JSON request from a surface service to core-api: a same-origin `/v1/...` path through the
 * policy's `platformFetch`, which attaches the live session bearer and bounds the wait for the
 * headers of each attempt. The body read after the headers has its own bound
 * (`createBodyDeadline`), so a call settles in bounded time per attempt plus one body read.
 *
 * The caller names the response type as the type argument at the call site, with the path literal
 * as the first argument, so the route and body parity scans read the call. The answer is
 * untrusted: a caller validates any member it uses.
 *
 * - A 2xx answer resolves the parsed JSON body.
 * - A non-2xx answer rejects with {@link ApiError} carrying the status.
 * - A body that is not JSON rejects with the parse error.
 * - A body that does not end within the deadline after the headers rejects with a `TimeoutError`.
 * - A caller signal that aborts rejects with its reason, before or during the body read.
 * - A `platformFetch` rejection (its header deadline included) rejects with that error.
 *
 * A caller sets no `Authorization` header.
 */
export type CoreApiRequest = <T>(path: string, init: RequestInit) => Promise<T>;

/**
 * Build a {@link CoreApiRequest}.
 *
 * @param platformFetch The policy's `platformFetch`.
 * @param timeoutMs The bound on the body read after the headers, in milliseconds;
 *   {@link SERVICES_FETCH_TIMEOUT_MS} unless a test shortens it. A value out of range rejects each
 *   call with a `RangeError` and sends nothing.
 * @param attemptCheck Read once per network attempt, when `platformFetch` copies the request's
 *   `init` for that attempt: the `signal` member is a getter. `null` lets the attempt go. A signal
 *   joins the attempt's signals, so an aborted one rejects the attempt with its reason and sends
 *   nothing. Absent, every attempt goes.
 *
 * The body timer starts only after the headers are in, so no retry before the headers sees a
 * spent signal.
 */
export function createCoreApiRequest(
  platformFetch: typeof fetch,
  timeoutMs: number = SERVICES_FETCH_TIMEOUT_MS,
  attemptCheck: () => AbortSignal | null = () => null,
): CoreApiRequest {
  return async <T>(path: string, init: RequestInit): Promise<T> => {
    const deadline = createBodyDeadline(timeoutMs);
    const checked: RequestInit = { ...init };
    Object.defineProperty(checked, 'signal', {
      configurable: true,
      enumerable: true,
      get: () => deadline.join(init.signal, attemptCheck()),
    });
    const res = await platformFetch(path, checked);
    if (!res.ok) throw new ApiError(res.status, `${path} answered ${res.status}`, null);
    const body: T = await deadline.read<T>(() => res.json());
    return body;
  };
}

/**
 * Read the target of a mount's `fetch` call once, in the form the network call will send.
 *
 * - A string is taken as it is.
 * - A `Request` of this realm is copied with `new Request(input)`. The copy takes the URL, the method, the
 *   headers, the body and the signal of the input, and its `url` is the platform's own reader, so
 *   a subclass that overrides `url` cannot show the classifier one URL and send another. A body
 *   moves to the copy, as it moves into the request the global `fetch` makes.
 * - Any other value, a `URL` among them, becomes `String(input)`, the conversion the global
 *   `fetch` applies. The string is immutable, so a later change to the object changes nothing.
 *
 * It throws what the read throws: a `toString` that throws, or the `TypeError` of `new Request`
 * for a `Request` whose body a reader used. It is the shell's `readFetchTarget` (WS-16b). A caller
 * checks {@link isForeignRequest} first, because a `Request` of another realm reads here as the text
 * `[object Request]`.
 */
function readMountTarget(input: RequestInfo | URL): string | Request {
  if (typeof input === 'string') return input;
  if (input instanceof Request) return new Request(input);
  return String(input);
}

/**
 * The `fetch` of one mount.
 *
 * - After the mount ends, every call rejects with `SurfaceServiceRefusedError` `disposed` and
 *   sends nothing.
 * - A `Request` of another realm (see {@link classifyFetchTarget}) goes to the global `fetch` as it
 *   is, with `init`, and the policy never sees it: this realm reads no URL from it and puts no
 *   bearer on it (R-125). Its method, headers and body therefore reach the network unchanged. A
 *   realm check that throws, as a `Proxy` can, rejects the call with that error and sends nothing.
 *   It is the shell's `withFetchTarget`.
 * - Each call reads its target once (a string as it is, a `Request` of this realm as a copy, any other value by
 *   `String(input)`, the conversion the global `fetch` applies) and classifies that one value with
 *   the rule of {@link classifyFetchTarget} over `documentHref()`. A read that throws rejects the
 *   call with that error and sends nothing, as the global `fetch` does.
 * - The call sends the value it classified, never the caller's object. A string target goes as
 *   the absolute URL it resolved to against `documentHref()`, so neither a `<base>` element nor
 *   an object whose `toString` answers another URL can move a classified call to another origin
 *   (security branch S3). A `Request` target goes as its copy, whose URL is already absolute.
 * - A `'cross-origin'` target goes to the global `fetch`. The policy never sees it, so no policy
 *   can put a bearer on another origin.
 * - Every other target, `'kernel-only'` included, goes to `policy.appFetch(scope.request.appUuid,
 *   attemptTimeoutMs)`, which the mount takes once, at creation. The policy rules the bearer and
 *   the `'kernel-only'` refusal.
 * - A system mount (`placement.kind === 'system'`) hands the policy `timeoutMs` as
 *   `attemptTimeoutMs`, so each network attempt of a same-origin call waits at most `timeoutMs`
 *   for its headers, and each retry has a fresh deadline (R-127). Every other mount hands
 *   `undefined`, so a hosted app's stream stays open for as long as its server sends it. A `null`
 *   `appUuid` does not mark a system mount: an interim root and a catalog-less app have one too.
 *   The mount sets no deadline of its own over the whole call.
 * - The mount hands `init` to the policy, or to the global `fetch`, unchanged. What the policy
 *   then sends is the policy's rule (see `OriginFetchPolicy.appFetch`).
 *
 * @param scope The mount's scope.
 * @param policy The host's policy for this origin.
 * @param documentHref Reads the document URL at each call, `() => location.href` in production.
 * @param timeoutMs The per-attempt header deadline of a system mount's same-origin call, in
 *   milliseconds; {@link SERVICES_FETCH_TIMEOUT_MS} unless a test shortens it. Unread for any other
 *   mount.
 * @throws `RangeError` at creation for a system mount whose `timeoutMs` is out of range.
 *
 * It holds no state between calls.
 */
export function createMountFetch(
  scope: MountScope,
  policy: OriginFetchPolicy,
  documentHref: () => string,
  timeoutMs: number = SERVICES_FETCH_TIMEOUT_MS,
): typeof fetch {
  const system = scope.request.placement.kind === 'system';
  if (system) assertTimerDelay(timeoutMs);
  const sameOrigin = policy.appFetch(scope.request.appUuid, system ? timeoutMs : undefined);
  return (input, init) => {
    if (scope.disposed) return Promise.reject(disposedError('fetch'));
    let read: string | Request;
    try {
      if (typeof input !== 'string' && isForeignRequest(input)) return fetch(input, init);
      read = readMountTarget(input);
    } catch (err) {
      return Promise.reject(err);
    }
    const resolved = resolveTarget(typeof read === 'string' ? read : read.url, documentHref());
    const send = typeof read === 'string' ? resolved.href : read;
    return resolved.target === 'cross-origin' ? fetch(send, init) : sameOrigin(send, init);
  };
}
